// src/intervenciones/iniciarIntervencion.jsx
import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { ticajeStore } from "../data/ticajeStore";

/**
 * IniciarIntervencion
 * - El padre solo lo renderiza cuando NO hay intervención activa.
 * - onSuccess/onError -> para toasts en el padre.
 * - Bloqueo optimista (isSubmitting) y normalización de IDs.
 * - Deriva cliente/centros al seleccionar OT, y resetea si cambia cliente manualmente.
 * - Usa ticajeStore para:
 *    • cargar catálogos (ordenes, clientes, centros-por-OT) offline-first
 *    • iniciar intervención (decidiendo online/offline + outbox)
 */
export const IniciarIntervencion = ({
  onTicajeActualizado,
  onSuccess,
  onError,
  uiLocked = false,
}) => {
  // Catálogos
  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [centros, setCentros] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  // Selecciones
  const [cliente, setCliente] = useState("");                  // id cliente (string para select)
  const [clienteSeleccionado, setClienteSeleccionado] = useState(""); // controlado por react-select
  const [orden, setOrden] = useState("");                      // id OT (string)
  const [centro, setCentro] = useState("");                    // id centro (string)

  // Extras
  const [comentarios, setComentarios] = useState("");
  const [mensaje, setMensaje] = useState("");                  // mensajes cortos de validación
  const [coordenadas, setCoordenadas] = useState({ latitud: null, longitud: null });
  const [inicio, setInicio] = useState(null);
  const [descripcionOT, setDescripcionOT] = useState("");

  // Flags/Refs
  const [isSubmitting, setIsSubmitting] = useState(false);
  const clienteCambiadoManualmente = useRef(false);
  const limpiarMensajeTimeout = useRef(null);

  // ---------------------------------------------------------------------------
  // Catálogos (ordenes, clientes) vía store (offline-first)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let alive = true;

    const loadCatalogos = async () => {
      setLoadingCatalogos(true);
      try {
        const [ords, clis] = await Promise.all([
          ticajeStore.getOrdenes(),
          ticajeStore.getClientes(),
        ]);

        if (!alive) return;
        setOrdenes(Array.isArray(ords) ? ords : []);
        setClientes(Array.isArray(clis) ? clis : []);
      } catch (err) {
        console.error("[IniciarIntervencion] Error cargando catálogos:", err);
        if (!alive) return;
        setOrdenes([]);
        setClientes([]);
      } finally {
        if (alive) setLoadingCatalogos(false);
      }
    };

    loadCatalogos();

    return () => {
      alive = false;
      if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    };
  }, []);

  // Centros por OT, vía store
  const fetchCentrosPorOrden = async (ordenId) => {
    try {
      const data = await ticajeStore.getCentrosPorOrden(ordenId);
      const centrosData = Array.isArray(data) ? data : data?.data || [];
      setCentros(centrosData);
    } catch (err) {
      console.error("[IniciarIntervencion] Error al cargar centros de la OT:", err);
      setCentros([]);
    }
  };

  // ---------------------------------------------------------------------------
  // Reacciones a cambios
  // ---------------------------------------------------------------------------

  // Cuando cambia la OT, derivamos cliente, descripción y centros
  useEffect(() => {
    const ordenSeleccionada = ordenes.find((o) => o.id === Number(orden));
    if (!ordenSeleccionada) {
      setCliente("");
      setClienteSeleccionado("");
      setDescripcionOT("");
      setCentros([]);
      return;
    }
    const cid = String(ordenSeleccionada.cliente_id ?? "");
    setCliente(cid);
    setClienteSeleccionado(cid);
    setDescripcionOT(ordenSeleccionada.descripcion || "");
    fetchCentrosPorOrden(ordenSeleccionada.id);
  }, [orden, ordenes]);

  // Si cambia el cliente manualmente, limpiamos OT/centro/desc
  useEffect(() => {
    if (!clienteSeleccionado) return;
    if (clienteCambiadoManualmente.current) {
      setOrden("");
      setCentro("");
      setDescripcionOT("");
      setCentros([]);
      setCliente(clienteSeleccionado);
    }
    clienteCambiadoManualmente.current = false;
  }, [clienteSeleccionado]);

  // Filtrado de OTs por cliente seleccionado
  const ordenesFiltradas = clienteSeleccionado
    ? ordenes.filter((o) => o.cliente_id === Number(clienteSeleccionado))
    : ordenes;

  // ---------------------------------------------------------------------------
  // Utilidades
  // ---------------------------------------------------------------------------
  const calcularDistancia = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const setMensajeTemporal = (txt) => {
    setMensaje(txt);
    if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    limpiarMensajeTimeout.current = setTimeout(() => setMensaje(""), 4500);
  };

  // ---------------------------------------------------------------------------
  // Handler principal: inicia intervención
  // ---------------------------------------------------------------------------
  const capturarUbicacionYCrearTicaje = () => {
    // ⚠️ Evita duplicar inicio si ya hay intervención activa (local o servidor)
    const snap = ticajeStore.snapshot ? ticajeStore.snapshot() : { active: null, local: null };
    const active = snap.active || snap.local;
    if (active && !active?.pendingFinish && !active?.pendingCancel) {
      onError?.("Ya tienes una intervención en curso. Ve a 'Finalizar' para cerrarla.");
      onTicajeActualizado?.();
      return;
    }

    // Validaciones de formulario
    if (!orden || !centro) {
      setMensajeTemporal("Selecciona una orden de trabajo y un centro.");
      return;
    }
    if (isSubmitting || uiLocked) return;

    const centroSeleccionado = centros.find((c) => c.id === Number(centro));
    if (
      !centroSeleccionado ||
      !centroSeleccionado.latitud ||
      !centroSeleccionado.longitud
    ) {
      setMensajeTemporal("Este centro no tiene coordenadas asignadas.");
      return;
    }

    if (!navigator.geolocation) {
      onError?.("Tu navegador no soporta geolocalización.");
      return;
    }

    setIsSubmitting(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const latitud = position.coords.latitude;
          const longitud = position.coords.longitude;

          const distancia = calcularDistancia(
            latitud,
            longitud,
            parseFloat(centroSeleccionado.latitud),
            parseFloat(centroSeleccionado.longitud)
          );

          // Confirmación si está a más de 1 km
          if (distancia > 1) {
            const confirmar = window.confirm(
              `Estás a ${distancia.toFixed(2)} km del centro. ¿Deseas iniciar igualmente?`
            );
            if (!confirmar) {
              setMensajeTemporal("Ticaje cancelado por el usuario.");
              setIsSubmitting(false);
              return;
            }
          }

          // Fingerprint del dispositivo
          const fpInstance = await FingerprintJS.load();
          const result = await fpInstance.get();
          const fingerprint = result.visitorId;

          const clienteIdNum = Number(cliente) || null;
          const centroIdNum  = Number(centro) || null;
          const ordenIdNum   = Number(orden) || null;

          const cliObj = clientes.find((c) => c.id === clienteIdNum);
          const ordObj = ordenes.find((o) => o.id === ordenIdNum);

          const clienteNombre = cliObj?.nombre || "";
          const centroNombre  = centroSeleccionado?.nombre || "";
          const ordenTitulo   = ordObj?.titulo || "";

          // ✅ delega en la store: ella decide online vs offline y gestiona outbox
          const resultStore = await ticajeStore.start({
            clienteId: clienteIdNum,
            centroId: centroIdNum,
            ordenId: ordenIdNum,
            ordenTitulo,
            clienteNombre,
            centroNombre,
            comentarios: comentarios || "",
            latitud,
            longitud,
            fingerprint,
          });

          setInicio(new Date());
          setCoordenadas({ latitud, longitud });

          if (resultStore?.status === "online") {
            onSuccess?.("Ticaje iniciado correctamente.");
          } else if (resultStore?.status === "offline") {
            onSuccess?.("Sin conexión: ticaje guardado y se enviará automáticamente.");
          } else if (resultStore?.status === "noop") {
            onError?.("Ya existe una intervención activa, no se ha iniciado otra.");
          } else {
            // Por si acaso: lo tratamos como éxito genérico
            onSuccess?.("Ticaje iniciado.");
          }

          await onTicajeActualizado?.(); // el padre ocultará este componente
        } catch (error) {
          console.error("❌ Error al iniciar ticaje (store):", error);
          onError?.("Error al registrar el ticaje.");
        } finally {
          setIsSubmitting(false);
        }
      },
      (error) => {
        console.error("❌ Error de geolocalización:", error);
        onError?.("No se pudo obtener la ubicación. Revisa los permisos del navegador.");
        setIsSubmitting(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Limpieza del formulario
  const cancelarFormulario = () => {
    setCliente("");
    setClienteSeleccionado("");
    setCentro("");
    setComentarios("");
    setMensaje("");
    setDescripcionOT("");
    setCoordenadas({ latitud: null, longitud: null });
    setInicio(null);
    setOrden("");
    setCentros([]);
  };

  const botonDeshabilitado =
    isSubmitting || !orden || !centro || loadingCatalogos || uiLocked;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="formulario-ticaje">
      <h2 className="centrar-titulo">Iniciar Intervención</h2>

      {/* Mensajes de validación propios del formulario */}
      {mensaje && <p className="rojo">{mensaje}</p>}

      {/* Aviso si no hay catálogos y estás offline */}
      {!loadingCatalogos &&
        !ordenes?.length &&
        !clientes?.length &&
        !navigator.onLine && (
          <p className="rojo" style={{ marginTop: 8 }}>
            No hay datos de órdenes/clientes en caché. Abre la app con conexión al menos
            una vez para precargarlos.
          </p>
        )}

      <form
        onSubmit={(e) => e.preventDefault()}
        className="form-agregar-tag registro-fecha"
      >
        {/* CLIENTE */}
        <div className="form-group">
          <label>Cliente:</label>
          <Select
            options={clientes
              .filter((c) => ordenes.some((o) => o.cliente_id === c.id))
              .map((c) => ({ value: c.id, label: c.nombre }))}
            value={
              clienteSeleccionado
                ? {
                    value: clienteSeleccionado,
                    label:
                      clientes.find((c) => c.id === Number(clienteSeleccionado))
                        ?.nombre || "",
                  }
                : null
            }
            onChange={(selected) => {
              clienteCambiadoManualmente.current = true;
              setClienteSeleccionado(selected ? String(selected.value) : "");
            }}
            isClearable
            placeholder={
              loadingCatalogos ? "Cargando clientes..." : "Selecciona un cliente"
            }
            isDisabled={loadingCatalogos || uiLocked}
            className="select-cliente"
            classNamePrefix="select"
            aria-label="Seleccionar cliente"
            inputId="select-cliente"
          />
        </div>

        {/* ORDEN DE TRABAJO */}
        <div className="form-group">
          <Select
            options={ordenesFiltradas.map((o) => ({
              value: o.id,
              label: o.titulo,
            }))}
            value={
              orden
                ? {
                    value: orden,
                    label:
                      ordenes.find((o) => o.id === Number(orden))?.titulo || "",
                  }
                : null
            }
            onChange={(selected) =>
              setOrden(selected ? String(selected.value) : "")
            }
            isClearable
            placeholder={loadingCatalogos ? "Cargando OTs..." : "Selecciona una OT"}
            isDisabled={loadingCatalogos || uiLocked}
            className="select-orden"
            classNamePrefix="select"
            aria-label="Seleccionar orden de trabajo"
            inputId="select-orden"
          />

          {descripcionOT && (
            <div
              className="descripcion-ot"
              style={{ marginTop: "0.5em", fontStyle: "italic", color: "#555" }}
            >
              <strong>Descripción:</strong> {descripcionOT}
            </div>
          )}
        </div>

        {/* CENTRO DE TRABAJO */}
        <div className="form-group">
          <label htmlFor="select-centro">Centro de trabajo:</label>
          <select
            id="select-centro"
            value={centro}
            onChange={(e) => setCentro(e.target.value)}
            className="input-layout"
            disabled={!orden || centros.length === 0 || loadingCatalogos || uiLocked}
            aria-disabled={!orden || centros.length === 0 || loadingCatalogos || uiLocked}
          >
            <option value="">
              {loadingCatalogos ? "Cargando..." : "Selecciona un centro"}
            </option>
            {centros.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </div>

        {/* COMENTARIOS */}
        <div className="form-group">
          <label htmlFor="comentarios">Comentarios:</label>
          <textarea
            id="comentarios"
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
            className="input-layout"
            placeholder="Opcional"
            disabled={uiLocked}
          ></textarea>
        </div>

        {/* ACCIONES */}
        <div className="form-group" style={{ width: "100%" }}>
          <button
            type="button"
            className="btn-login"
            onClick={capturarUbicacionYCrearTicaje}
            disabled={botonDeshabilitado}
            aria-busy={isSubmitting}
          >
            {isSubmitting ? "Iniciando..." : "Capturar Ubicación e Iniciar"}
          </button>

          <button
            type="button"
            className="btn-registrate"
            onClick={cancelarFormulario}
            style={{ marginTop: 10 }}
            disabled={isSubmitting || loadingCatalogos || uiLocked}
          >
            Limpiar
          </button>
        </div>

        {/* Info de depuración (opcional) */}
        {inicio && (
          <p style={{ fontSize: 12, marginTop: 10 }}>
            Inicio local: {new Date(inicio).toLocaleString()}
            {coordenadas.latitud && (
              <>
                {" "}
                — ({coordenadas.latitud.toFixed(5)},{" "}
                {coordenadas.longitud?.toFixed(5)})
              </>
            )}
          </p>
        )}
      </form>
    </div>
  );
};
